import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { portfolios } from '@/mocks/portfolioData';

type Portfolio = (typeof portfolios)[0];

interface PortfolioDetailModalProps {
  portfolio: Portfolio;
  onClose: () => void;
}

export default function PortfolioDetailModal({ portfolio, onClose }: PortfolioDetailModalProps) {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<'info' | 'files'>('info');

  const fileCategories = [
    { name: 'Tapu', icon: 'ri-file-text-line', count: 1, files: ['Tapu_Scan_2026.pdf'] },
    { name: 'Fotoğraflar', icon: 'ri-image-line', count: portfolio.photos, files: ['foto_01.jpg', 'foto_02.jpg', 'foto_03.jpg'] },
    { name: 'Belgeler', icon: 'ri-folder-line', count: portfolio.documents, files: ['Isinma_Raporu.pdf', 'Elektrik_Projesi.pdf'] },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative w-full max-w-2xl rounded-2xl shadow-glass max-h-[90vh] overflow-hidden ${
          isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
        }`}
      >
        {/* Image Header */}
        <div className="relative h-48 md:h-56">
          <img
            src={portfolio.photo}
            alt={portfolio.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-white text-lg md:text-xl font-semibold">{portfolio.title}</h2>
            <p className="text-white/80 text-sm mt-1">{portfolio.location}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <button
            onClick={() => setActiveTab('info')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'info'
                ? 'text-primary-500 border-b-2 border-primary-500'
                : isDark ? 'text-dark-muted' : 'text-light-muted'
            }`}
          >
            Bilgiler
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'files'
                ? 'text-primary-500 border-b-2 border-primary-500'
                : isDark ? 'text-dark-muted' : 'text-light-muted'
            }`}
          >
            Dosya Kasası
          </button>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6 max-h-[50vh] overflow-y-auto">
          {activeTab === 'info' && (
            <div className="space-y-5">
              {/* Price & Stats */}
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Fiyat</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {portfolio.price.toLocaleString('tr-TR')} ₺
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                    portfolio.status === 'Aktif'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    {portfolio.status}
                  </span>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                    portfolio.authorityStatus === 'Açık'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}>
                    {portfolio.authorityStatus}
                  </span>
                </div>
              </div>

              {/* Info Grid */}
              <div className={`grid grid-cols-2 gap-3 p-4 rounded-xl ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
                <div>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Mülk Tipi</p>
                  <p className={`text-sm font-medium mt-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {portfolio.propertyType}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Yetki Durumu</p>
                  <p className={`text-sm font-medium mt-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {portfolio.authorityStatus}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Görüntülenme</p>
                  <p className={`text-sm font-medium mt-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {portfolio.viewCount} kez
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Eklenme Tarihi</p>
                  <p className={`text-sm font-medium mt-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {portfolio.createdAt}
                  </p>
                </div>
              </div>

              {/* View Badge */}
              <div className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <i className="ri-eye-line text-primary-500" />
                </div>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    Paylaşılan Link Takibi
                  </p>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {portfolio.viewCount} görüntüleme · Son 7 günde +12
                  </p>
                </div>
                <span className="ml-auto text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Aktif
                </span>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="space-y-4">
              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                  isDark
                    ? 'border-dark-border hover:border-primary-500/50'
                    : 'border-light-border hover:border-primary-500/50'
                }`}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-primary-500 text-xl" />
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  Dosya Yükle
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  PDF, JPG, PNG (max 10MB)
                </p>
              </div>

              {/* File Categories */}
              <div className="space-y-3">
                {fileCategories.map((cat) => (
                  <div
                    key={cat.name}
                    className={`p-4 rounded-xl border ${isDark ? 'border-dark-border' : 'border-light-border'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                          <i className={`${cat.icon} text-primary-500`} />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                            {cat.name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                            {cat.count} dosya
                          </p>
                        </div>
                      </div>
                      <button className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                        isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
                      }`}>
                        <i className="ri-add-line" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {cat.files.map((file, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                            isDark ? 'bg-dark-bg' : 'bg-light-bg'
                          }`}
                        >
                          <i className="ri-file-line text-xs text-primary-500" />
                          <span className={`flex-1 truncate ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                            {file}
                          </span>
                          <button className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                            isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
                          }`}>
                            <i className="ri-download-line text-xs" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}